import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, UseFormSetValue } from 'react-hook-form';
import {
  AppButton,
  AppCheckbox,
  AppForm,
  AppFormLabel,
  AppInputNumber,
  AppSelect,
  AppTextArea,
} from '@/components/atoms';
import RA2NDatePicker from '@/components/organisms/RA2NDatePicker/RA2NDatePicker';
import type { TreatmentReportFormData } from '@/schemas/treatmentReportSchema';
import { warekiMonthToYearmonth, yearmonthToDateStr } from '@/utils/dateConverter';
import type { TreatmentReportLabelChecks, TreatmentReportOption } from '../types';
import { TreatmentReportFieldRow } from './TreatmentReportFieldRow';

interface TreatmentReportBasicFieldsProps {
  activeConsentContent: string | null;
  control: Control<TreatmentReportFormData>;
  disabled: boolean;
  doctorOptions: TreatmentReportOption[];
  handleCalcFrequency: () => Promise<void>;
  hospitalOptions: TreatmentReportOption[];
  isCalcLoading: boolean;
  labelChecks: TreatmentReportLabelChecks;
  markFrequencyTouched: (value: unknown) => void;
  onOpenHospitalModal: () => void;
  onOpenHospitalSearch: () => void;
  onToggleLabel: (key: string) => void;
  setValue: UseFormSetValue<TreatmentReportFormData>;
  showSecondFlag: boolean;
  watchedHospitalId?: number | null;
}

export const TreatmentReportBasicFields: React.FC<TreatmentReportBasicFieldsProps> = ({
  activeConsentContent,
  control,
  disabled,
  doctorOptions,
  handleCalcFrequency,
  hospitalOptions,
  isCalcLoading,
  labelChecks,
  markFrequencyTouched,
  onOpenHospitalModal,
  onOpenHospitalSearch,
  onToggleLabel,
  setValue,
  showSecondFlag,
  watchedHospitalId,
}) => (
  <>
    <div className="flex items-center gap-2">
      <AppFormLabel label="施術年月" required className="ml-[24.4px] flex-1" />
    </div>
    <div className="flex min-w-0 flex-wrap items-start gap-4 md:gap-6">
      <Controller
        name="treatment_yearmonth"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
            <RA2NDatePicker
              value={yearmonthToDateStr(field.value)}
              onChange={val => {
                if (val) {
                  const ym = warekiMonthToYearmonth(val);
                  setValue('treatment_yearmonth', (ym ?? null) as any, { shouldDirty: true });
                } else {
                  setValue('treatment_yearmonth', null as any, { shouldDirty: true });
                }
              }}
              picker="month"
              placeholder="年月指定"
              disabled={disabled}
              error={!!error}
            />
          </AppForm.Item>
        )}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Controller
          name="is_treatment_report_default"
          control={control}
          render={({ field }) => (
            <AppCheckbox
              checked={field.value}
              disabled={disabled}
              onChange={e => field.onChange(e.target.checked)}
            >
              施術報告書交付料を算定しない
            </AppCheckbox>
          )}
        />
        {showSecondFlag && (
          <Controller
            name="second_flag"
            control={control}
            render={({ field }) => (
              <AppCheckbox
                checked={!!field.value}
                disabled={disabled}
                onChange={e => field.onChange(e.target.checked)}
              >
                エラーを無視して作成する
              </AppCheckbox>
            )}
          />
        )}
      </div>
    </div>

    <TreatmentReportFieldRow
      labelKey="treatment_report_date"
      label="作成年月日"
      required
      checked={labelChecks.treatment_report_date}
      onToggle={onToggleLabel}
    >
      <Controller
        name="treatment_report_date"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
            <RA2NDatePicker
              value={field.value || ''}
              onChange={val => field.onChange(val ?? '')}
              placeholder="元号00年00月00日"
              disabled={disabled}
              error={!!error}
              className="w-[214px] max-w-full"
            />
          </AppForm.Item>
        )}
      />
    </TreatmentReportFieldRow>

    <div className="flex items-center gap-2">
      <AppFormLabel label="医療機関" className="ml-[24.4px] flex-1" />
    </div>
    <div>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <Controller
          name="hospital_id"
          control={control}
          render={({ field }) => (
            <AppSelect
              value={field.value ?? ''}
              onChange={val => {
                field.onChange(val === '' ? null : val);
                setValue('doctor_id', null, { shouldDirty: true });
              }}
              options={hospitalOptions}
              placeholder="医療機関選択"
              className="w-[214px] max-w-full"
              disabled={disabled}
              allowClear={false}
            />
          )}
        />
        <AppButton type="primary" disabled={disabled} onClick={onOpenHospitalSearch}>
          医療機関
        </AppButton>
      </div>
    </div>

    <TreatmentReportFieldRow
      labelKey="doctor_name"
      label="担当医師名"
      checked={labelChecks.doctor_name}
      onToggle={onToggleLabel}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <Controller
          name="doctor_id"
          control={control}
          render={({ field }) => (
            <AppSelect
              value={field.value ?? ''}
              onChange={val => field.onChange(val === '' ? null : val)}
              options={doctorOptions}
              placeholder="担当医師選択"
              className="w-[214px] max-w-full"
              disabled={disabled || !watchedHospitalId}
              allowClear={false}
            />
          )}
        />
        <AppButton
          type="primary"
          disabled={disabled || !watchedHospitalId}
          onClick={onOpenHospitalModal}
        >
          医師新規追加
        </AppButton>
      </div>
    </TreatmentReportFieldRow>

    <TreatmentReportFieldRow
      labelKey="contents_frequency"
      label="施術の内容"
      checked={labelChecks.contents_frequency}
      onToggle={onToggleLabel}
    >
      <Controller
        name="contents_frequency"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
            {activeConsentContent && (
              <div className="mb-2 whitespace-pre-wrap">{activeConsentContent}</div>
            )}
            <AppTextArea
              {...field}
              value={field.value ?? ''}
              rows={3}
              maxLength={300}
              disabled={disabled}
            />
          </AppForm.Item>
        )}
      />
    </TreatmentReportFieldRow>

    <TreatmentReportFieldRow
      labelKey="treatment_monthly_avg"
      label="施術の頻度"
      required
      checked={labelChecks.treatment_monthly_avg}
      onToggle={onToggleLabel}
    >
      <Controller
        name="treatment_monthly_avg"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-sm">月 平均</span>
              <AppInputNumber
                min={0}
                max={999}
                precision={0}
                value={field.value}
                onChange={val => {
                  field.onChange(val);
                  markFrequencyTouched(val);
                }}
                disabled={disabled}
                className="w-[100px]"
              />
              <span className="text-sm">回</span>
              <AppButton
                type="primary"
                disabled={disabled || isCalcLoading}
                loading={isCalcLoading}
                onClick={handleCalcFrequency}
              >
                再取得
              </AppButton>
            </div>
            <span className="text-xs text-gray-500">回数の修正が必要な場合は手入力して下さい</span>
          </AppForm.Item>
        )}
      />
    </TreatmentReportFieldRow>

    <TreatmentReportFieldRow
      labelKey="status_course"
      label="患者の状態・経過"
      required
      checked={labelChecks.status_course}
      onToggle={onToggleLabel}
    >
      <Controller
        name="status_course"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
            <AppTextArea
              {...field}
              value={field.value ?? ''}
              rows={3}
              maxLength={300}
              disabled={disabled}
            />
          </AppForm.Item>
        )}
      />
    </TreatmentReportFieldRow>

    <TreatmentReportFieldRow
      labelKey="remarks"
      label="特記すべき事項"
      checked={labelChecks.remarks}
      onToggle={onToggleLabel}
    >
      <Controller
        name="remarks"
        control={control}
        render={({ field }) => (
          <AppTextArea
            {...field}
            value={field.value ?? ''}
            rows={3}
            maxLength={300}
            disabled={disabled}
          />
        )}
      />
    </TreatmentReportFieldRow>
  </>
);
